import moment from "moment-timezone";
import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useGet, usePatch } from "redux/fetch-hooks";

import { heapTrack } from "lib/analytics";
import {
    APPLICATION_STATE,
    PIPELINE_APPLICATION_TYPE_LOOKUP,
    REJECTION_REASON,
} from "lib/constants";

import {
    applicationPatchError,
    applicationPatchFinished,
    applicationPatchStart,
    applicationPatchSuccess,
    applicationSetState,
    applicationsMerge,
    hireSetData,
    hiresMerge,
} from "./actions";

/**
 * Fetches applications by the type of application
 * (unreviewed, invited, interviewed, pending, hired)
 */
export function useFetchApplicationsByType(applicationType) {
    const slug = PIPELINE_APPLICATION_TYPE_LOOKUP[applicationType].toLowerCase();

    const [fetchApps, options] = useGet(`/api/v1/pipeline/${slug}/`, {
        reduxSuccess: [
            (result) => applicationsMerge(result.applications),
            (result) => hiresMerge(result.hires),
        ],
    });

    return [
        ({ permission = null, positionIDs = [], ...args }) => {
            const data = {};

            if (permission) {
                data.permission = permission;
            }

            if (positionIDs.length > 0) {
                data.pids = positionIDs.join(",");
            }

            fetchApps({
                data,
                ...args,
            });
        },
        options,
    ];
}

function formatAppData(
    origData,
    { rejectReasons = [], otherReason = null, reschedule = false } = {},
) {
    const data = { ...origData };

    if (data.state === APPLICATION_STATE.REJECTED) {
        /* eslint-disable no-bitwise */
        data.reason = rejectReasons.reduce((memo, reason) => {
            const num = Number(reason);
            return memo | num;
        }, 0);
        /* eslint-enable */

        if (otherReason) {
            data.reason_other = otherReason;
        }

        if (reschedule) {
            data.reschedule = true;
        }
    }

    return data;
}

/**
 * Patches an application
 */
export function usePatchApplication({ useHandlers = true, setState = false } = {}) {
    let patchOptions = {};
    if (useHandlers) {
        patchOptions = {
            reduxStart: applicationPatchStart,
            reduxError: (error) => applicationPatchError(error),
            reduxSuccess: (result) => applicationPatchSuccess(result.id),
            reduxComplete: applicationPatchFinished,
        };
    }

    const uid = useSelector((state) => state.ws.permissionUser);
    const [runPatch, patchFuncs] = usePatch(null, patchOptions);
    const dispatch = useDispatch();

    return [
        (applicationID, { data, metadata }, options) => {
            const url = `/api/v1/application/${applicationID}/`;

            const patchData = formatAppData(data, metadata);

            heapTrack("Application Patch", {
                ...patchData,
                id: applicationID,
                auth_uid: uid,
            });

            if (setState) {
                const opts = {};
                if (data.state === APPLICATION_STATE.REJECTED) {
                    opts.time_rejected = moment.utc().toISOString();
                }

                dispatch(
                    applicationSetState({
                        id: applicationID,
                        state: data.state,
                        rejectReasons: metadata.rejectReasons,
                        options: opts,
                    }),
                );
            }

            runPatch({
                url,
                data: patchData,
                ...options,
            });
        },
        patchFuncs,
    ];
}

export function usePatchHire({ setState = true } = {}) {
    const dispatch = useDispatch();
    const hires = useSelector((state) => state.hires);
    const [runPatch, patchFuncs] = usePatch();

    return [
        (hireID, data, options = {}) => {
            const url = `/api/v1/hire/${hireID}/`;

            if (setState) {
                const hire = hires[hireID];
                if (hire) {
                    dispatch(
                        hireSetData({
                            ...hire,
                            ...data,
                        }),
                    );
                }
            }

            runPatch({
                url,
                data,
                ...options,
            });
        },
        patchFuncs,
    ];
}

export function useConfirmAbsent(applicationID) {
    const dispatch = useDispatch();
    const [patchApp, { isLoading }] = usePatchApplication();

    const confirmAbsent = useCallback(() => {
        if (isLoading) {
            return;
        }

        dispatch(
            applicationSetState({
                id: applicationID,
                state: APPLICATION_STATE.REJECTED,
                rejectReasons: [REJECTION_REASON.NO_SHOW],
                options: {
                    time_rejected: moment.utc().toISOString(),
                },
            }),
        );
        patchApp(applicationID, {
            data: {
                state: APPLICATION_STATE.REJECTED,
            },
            metadata: {
                rejectReasons: [REJECTION_REASON.NO_SHOW],
            },
        });
    }, [isLoading]);

    return [confirmAbsent, isLoading];
}
